import React, { useContext, useState, Suspense } from "react";
import { ISavedSearchItem } from "./save-search.types";
import {
  SaveSearchContext,
  SaveSearchProvider,
} from "./state/SaveSearchContext";
import { ViewportSizeType } from "../hooks/useViewport.hook";
import SaveThisSearchButton from "./SaveThisSearchButton";
import saveSearch from "./saveSearch";
import SaveSearchErrorModal from "./SaveSearchErrorModal";
import analytics from "../common/analytics/analytics";
import { IEventPushable } from "../common/analytics/analytics.types";
import { checkDegradedServicesStatus } from "../utils/checkDegradedStatus";
import { ErrorContext } from "../../src/context/ErrorContext";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import { StoreType } from "../StoreType";
import { useSelector } from "react-redux";
import { GET_STATE } from "../redux/slices/appSlice";

// Lazy imports
const SaveSearchModal = React.lazy(() => import("./SaveSearchModal"));
const SaveSearchConfirmationModal = React.lazy(
  () => import("./SaveSearchConfirmationModal")
);

interface SaveThisSearchProps {
  viewport: ViewportSizeType;
  savedSearches: ISavedSearchItem[];
}

const pushSaveSearchAnalytics = (): void => {
  analytics.push<IEventPushable>({
    event: {
      name: "click",
      category: "button",
      label: "save-search",
      action: "submit",
      section: "pop-up",
    },
  });
};

const pushSaveThisSearchAnalytics = (): void => {
  analytics.push<IEventPushable>({
    event: {
      name: "click",
      category: "link",
      label: "save-search",
      action: "start",
    },
  });
};

const pushErrorAnalytics = (): void => {
  analytics.push<IEventPushable>({
    event: {
      name: "error",
    },
    error: {
      message: "something-went-wrong",
    },
  });
};

const pushErrorAnalyticsSaveSearch = (): void => {
  analytics.push<IEventPushable>({
    event: {
      name: "error",
      category: "button",
      label: "save-search| retry-save",
      action: "select",
      section: "pop-up",
    },
    error: {
      message: "something-went-wrong",
    },
  });
};

const pushErrorAnalyticsSwitch = (userAction: string) => {
  if (userAction === "error-save") {
    return pushErrorAnalyticsSaveSearch();
  } else if (userAction === "error-back") {
    return pushErrorAnalytics();
  }
};

const SaveThisSearch: React.FC<SaveThisSearchProps> = ({
  savedSearches,
  viewport,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const context = useContext(SaveSearchContext);
  const errorContext = useContext(ErrorContext);

  const {
    app: { isToUseAuthZero },
  }: StoreType = useSelector(GET_STATE);

  const { accessToken } = useAuth(!isToUseAuthZero);

  async function checkAPIStatus() {
    return (await checkDegradedServicesStatus(accessToken, isToUseAuthZero))
      .degradedServices !== true
      ? openModal()
      : errorContext?.dispatch({
          type: "tracking",
        });
  }

  const openModal = () => {
    setIsModalOpen(true);
    pushSaveThisSearchAnalytics();
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmationModalClose = () => {
    context?.dispatch({ type: "confirmed" });
  };

  const handleSaveClick = async () => {
    if (context?.state.isValid) {
      setIsModalOpen(false);

      const state = context?.state;
      const searchName = state?.searchName;
      const notifyMe = state?.isAlerts;
      const deleteOnDeactivation = state?.deleteAlert;
      const deactivationDate = state?.alertDeactivationDate;

      let searchParams = new URL(window.location.href).searchParams;
      searchParams.delete("vrm");
      const url = searchParams.toString();

      const saveSearchData = {
        searchName,
        notifyMe,
        url,
        deleteOnDeactivation,
        deactivationDate,
        authToken: accessToken,
        isToUseAuthZero,
      };

      const res = await saveSearch(saveSearchData);
      pushSaveSearchAnalytics();
      if (res.status === 200) {
        context?.dispatch({ type: "saved" });
      } else {
        context.dispatch({ type: "error" });
      }
    }
  };

  return (
    <Suspense fallback={<></>}>
      <SaveThisSearchButton onClick={checkAPIStatus} />
      {isModalOpen && (
        <SaveSearchModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSaveClick={handleSaveClick}
          savedSearches={savedSearches}
          viewport={viewport}
        />
      )}

      {context?.state.isSaved && (
        <SaveSearchConfirmationModal
          isOpen={context?.state.isSaved}
          onClose={handleConfirmationModalClose}
        />
      )}

      {context?.state.isError && (
        <SaveSearchErrorModal
          isOpen={context?.state.isError}
          onClose={handleConfirmationModalClose}
          pushAnalyticsError={pushErrorAnalyticsSwitch}
          setIsModalOpen={setIsModalOpen}
        />
      )}
    </Suspense>
  );
};

/**
 * A proxy that wraps our component in a SaveSearchProvider, so the client of the
 * component doesn't need to know anything about the provider. As far as the client is
 * concerned they are rendering a SaveThisSearch component.
 */
const SaveThisSearchProxy: React.FC<SaveThisSearchProps> = ({
  savedSearches,
  viewport,
}) => {
  return (
    <SaveSearchProvider>
      <SaveThisSearch savedSearches={savedSearches} viewport={viewport} />
    </SaveSearchProvider>
  );
};

export default SaveThisSearchProxy;
